import React, { useEffect, useRef } from 'react';
import { Carousel } from '@zola/zola-ui/src/components/Carousel';
import cx from 'classnames';
import type { WCardDesignFamilySummaryView } from '@zola/svc-web-api-ts-client';
import { CardTile } from './CardTile';
import { WebsiteTile } from './WebsiteTile';
import styles from './SuiteCardsCarousel.module.less';
import { byCardType } from './utils';

interface SuiteCardsCarouselProps {
  isFirstRender: boolean;
  suites: WCardDesignFamilySummaryView[];
  activeColor: string;
  selectedSuiteIndex: number;
  selectedThemeKey?: string | null;
  hasWeddingAccount?: boolean;
  isMobileView?: boolean;
}

const SETTINGS_DESKTOP = {
  dots: true,
  arrows: true,
  infinite: true,
  swipeToSlide: true,
  slidesToShow: 5,
  slidesToScroll: 5,
  lazyLoad: false,
  useCSS: true,
  useTransform: false,
  adaptiveHeight: false,
  responsive: [
    {
      breakpoint: 1280,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 4,
      },
    },
    {
      breakpoint: 1000,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
  ],
};
const SETTINGS_MOBILE = { ...SETTINGS_DESKTOP, arrows: false };

export const SuiteCardsCarousel: React.FC<SuiteCardsCarouselProps> = ({
  isFirstRender,
  suites,
  activeColor,
  selectedSuiteIndex,
  selectedThemeKey,
  hasWeddingAccount,
  isMobileView,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectedSuite = suites[selectedSuiteIndex];

  useEffect(() => {
    if (!isFirstRender) {
      wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [selectedSuite]);

  const carouselSuiteData =
    selectedSuite?.suites
      ?.sort(byCardType)
      .map((card, index) => (
        <CardTile key={card.uuid} card={card} activeColor={activeColor} position={index} />
      )) || [];

  const [defaultTheme] = Object.values(selectedSuite?.themePreviewImagesByThemeKey || {});

  const themeKeys = Object.keys(selectedSuite?.themePreviewImagesByThemeKey || {});
  const themes = themeKeys.map(themeKey => ({
    ...(selectedSuite?.themePreviewImagesByThemeKey?.[themeKey] || {}),
    label: themeKey,
  }));

  const selectedTheme = themes.find(
    theme =>
      theme.colorValue === activeColor ||
      theme.label.toLowerCase() === selectedThemeKey?.toLowerCase()
  );

  if (selectedTheme || defaultTheme) {
    const isSelected = Boolean(selectedThemeKey && themeKeys.includes(selectedThemeKey));
    const { previewImageUrl = '' } = selectedTheme || defaultTheme;

    carouselSuiteData.splice(
      1,
      0,
      <WebsiteTile
        isSelected={isSelected}
        key="website-tile"
        image={previewImageUrl}
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'label' does not exist on type 'WCardDesi...
        themeLabel={selectedTheme?.label || defaultTheme?.label}
        hasWeddingAccount={hasWeddingAccount}
      />
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={cx(styles.carouselWrapper, {
        [styles.position0]: selectedSuiteIndex % 4 === 0,
        [styles.position1]: selectedSuiteIndex % 4 === 1,
        [styles.position2]: selectedSuiteIndex % 4 === 2,
        [styles.position3]: selectedSuiteIndex % 4 === 3,
      })}
    >
      <Carousel
        padSlides
        useV2ArrowStyles
        useSmallerV2Arrows
        content={carouselSuiteData}
        settings={isMobileView ? SETTINGS_MOBILE : SETTINGS_DESKTOP}
      />
    </div>
  );
};
